import Layer from 'ol/layer/Layer';
import { Feature } from "ol";
import { Coordinate } from 'ol/coordinate';
import { WMSTileSize } from '../../components/sitebase/Base.types';
import type { AlertPoint } from '../efas/notifications/Notification.types';

export enum FeatureWindowPresentation {
    ONE = "O",
    LIST = "L",
    WINDOWS = "W",
}

export interface GeoPoint {
    type: string,
    coordinates: number[],
}

export interface FeatureInfoCallback {
    (featureInfo: string): void;
}

export interface PointsFeatureInfoUrlCallback {
    (featureInfo: { point: AlertPoint, url: string }[]): void;
}

export interface PointData {
    nuts_name: string
    location: { type: string, coordinates: number[] }
}

export interface MapLayer {
    name: string,
    points?: AlertPoint[],
    forecast_date?: string,
}

export interface WMSLayerDimension {
    "name": string
    "default": string
    "units": string
    "multipleValues": string
    "nearestValue": string
    "values": string
}
export interface WMSLayerAppSettings {
    AppSettings: {
        visibleInLayerSelector: boolean
    }
}
export interface WMSLayer {
    "@cascaded": string
    "@opaque": string
    "@queryable": string
    Abstract: string
    KeywordList: string[]
    Keywords: {
        queryable?: 'true' | 'false'
        public?: 'true' | 'false'
        group?: string
        group_icon?: 'STATIC' | 'FLOOD_SUMMARY' | 'EVALUATION'
        forecaster_layer?: 'true'
        internal_layer?: 'true' | 'false'
        visible_group?: string
        status?: 'off'
        layer_data?: 'vector' | 'vectortile' | 'imagestatic' | 'imagewms'
        layer_source?: string
        download?: 'true'
        until?: string
        label?: string
        animation_dimension?: string
        show_from?: string
        show_until?: string
        order?: string
        wms_gutter: string
    }
    Layer?: WMSLayer[]
    Name: string
    Title: string
    Dimension: WMSLayerDimension | WMSLayerDimension[]
    wmsInfo: WMSInfo
}
export interface WMSGetCapabilitiesDetail {
    "@version": string
    "@xmlns": string
    "@xmlns:xlink": string
    "@xmlns:xsi": string
    "@xsi:schemaLocation": string
    Service: {
        Name: string,
        Abstract: string,
        Title: string,
    }
    Capability: {
        Layer: {
            Layer: WMSLayer[]
        }
    }
}
export interface EFAS_WMSGetCapabilitiesI {
    status: number
    original_url: string
    getcapabilities: {
        ["WMS_Capabilities"]: WMSGetCapabilitiesDetail
    }
}



export interface GLOFAS_WMSGetCapabilitiesI {
    WMS_Capabilities: WMSGetCapabilitiesDetail
}


export interface WMSMap { [name: string]: WMSInfo }

export interface WMSInfo {
    name: string
    url: string
    order: number
    featureInfoFormat: string
    version: string
    latestForecastLayer: string
    latestForecastDate: string
    forecastLayerInfoFormat?: string
    forecastLayers?: string[]
    tileSize?: WMSTileSize
    gutter?: number
    layers: { [group: string]: WMSLayer[] }
}

export interface GetFeatureInfoData {
    id: string
    url: string
    marker: number
    name: string
    layerName: string
    forecastDate: string
    source: string
    coordinate: Array<number>
    data: unknown
    pp_tab?: unknown
    point?: unknown
    notificationpoint?: unknown
}

export interface FeatureInfoStoreData {
    name: string
    features: GetFeatureInfoData[]
    coordinate: Coordinate,
    icon: Feature,
}

export interface FeatureInfoStore {
    [id: number]: FeatureInfoStoreData
}

export interface ForecastDateStore {
    global: number,
    layers: {
        name: string,
        forecast_date: string
    }[]
}

export interface LegendDataItem {
    html_snippet: string
    layer: string
    layer_name: string
}

export type LegendData = LegendDataItem[];

export interface GlofasUserLayer {
    abstract: string
    bbox_string: string
    historical_legends: []
    id: number
    layer_visible: boolean
    legend_image: string
    menu_order: string
    name: string
    new: boolean
    object_id: number
    order: number
    ows: number
    ows_title: string
    parsed_default_timeposition: string
    parsed_timeposition: string | null
    projection: string | null
    queryable: boolean
    thumbnail: string
    timeposition: string
    title: string
    url: string
    user_category_id: number
    user_category_name: string
    visible: false
}

export interface MapActiveLayer {
    id: string,
    layer: Layer,
    source: string,
    name: string,
    group: string,
    displayName: string,
    order: number,
    dimensions: WMSLayerDimension | WMSLayerDimension[],
    layerInfo: WMSLayer,
}
export interface MapActiveLayers { [name: string]: MapActiveLayer }

export interface AoiData {
    aoi_id: string
    aoi_name: string
    creation_date: string
    customer_id: string
    description: string
    geoJSON: GeoPoint
    notification_active: boolean
    region: string
    twitter_notification_active: boolean
}

export interface EFASVersionApiResponse {
    body: string
    link_url: string
    title: string
    version: string
}

export interface AppShareState {
    layers: string
    c: string
    z: string
    forecast: string
    activenotif: string
    lview: string
    base_map?: string
}

export interface TimelineStateI { interval: string, layers: MapActiveLayer[] }